import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orderService } from '../services/order.service';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/Button';
import { Loader2, Package, ChevronRight } from 'lucide-react';

const statusColors: Record<string, string> = {
  PENDING: 'bg-yellow-500/10 text-yellow-500',
  PAID: 'bg-blue-500/10 text-blue-500',
  PROCESSING: 'bg-purple-500/10 text-purple-500',
  SHIPPED: 'bg-orange-500/10 text-orange-500',
  DELIVERED: 'bg-green-500/10 text-green-500',
  CANCELLED: 'bg-red-500/10 text-red-500',
  REFUNDED: 'bg-gray-500/10 text-gray-500',
};

export const OrdersPage: React.FC = () => {
  const [filter, setFilter] = useState<string>('ALL');

  const { data: orders, isLoading } = useQuery({
    queryKey: ['orders'],
    queryFn: orderService.getMyOrders,
  });

  const filteredOrders = orders?.filter((order) =>
    filter === 'ALL' ? true : order.status === filter
  );

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const statusOptions = [
    { value: 'ALL', label: 'All Orders' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'PAID', label: 'Paid' },
    { value: 'PROCESSING', label: 'Processing' },
    { value: 'SHIPPED', label: 'Shipped' },
    { value: 'DELIVERED', label: 'Delivered' },
    { value: 'CANCELLED', label: 'Cancelled' },
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-[rgb(var(--foreground))]">My Orders</h1>
      <p className="text-[rgb(var(--light-secondary))]">Track and manage your purchases</p>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap gap-2">
        {statusOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => setFilter(option.value)}
            className={`rounded-full px-4 py-1.5 text-sm transition-all ${
              filter === option.value
                ? 'bg-primary text-white'
                : 'border border-[rgb(var(--border))] text-[rgb(var(--foreground))] hover:bg-[rgb(var(--border))]'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {filteredOrders && filteredOrders.length > 0 ? (
        <div className="mt-6 space-y-4">
          {filteredOrders.map((order) => (
            <Link key={order.id} to={`/orders/${order.id}`}>
              <Card className="transition-all hover:shadow-md hover:-translate-y-0.5 cursor-pointer">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <div className="flex items-center space-x-3">
                        <span className="font-mono text-sm font-semibold text-primary">
                          #{order.orderNumber}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[order.status] || 'bg-gray-500/10 text-gray-500'}`}>
                          {order.status}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-[rgb(var(--light-secondary))]">
                        {order.items?.length || 0} items · {new Date(order.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="font-bold text-primary">
                        GHS {(order.total / 100).toFixed(2)}
                      </span>
                      <ChevronRight className="h-5 w-5 text-[rgb(var(--light-secondary))]" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <Card className="mt-6">
          <CardContent className="py-12 text-center">
            <Package className="mx-auto h-12 w-12 text-[rgb(var(--light-secondary))]" />
            <h3 className="mt-4 text-lg font-semibold text-[rgb(var(--foreground))]">No orders found</h3>
            <p className="text-[rgb(var(--light-secondary))]">
              {filter !== 'ALL' ? 'Try changing your filter' : 'Start shopping to place your first order'}
            </p>
            {filter !== 'ALL' && (
              <button
                onClick={() => setFilter('ALL')}
                className="mt-4 text-sm text-primary hover:underline"
              >
                Clear filter
              </button>
            )}
            {filter === 'ALL' && (
              <Link to="/shop">
                <Button variant="primary" className="mt-4">Start Shopping</Button>
              </Link>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};